import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory} from 'react-router-dom'

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Theme,
} from '@mui/material'
import {
  getProductLineColors,
  getSpacing,
  PGButton,
} from '@progynyglobal/pg-ui-library'
import {enqueueSnackbar} from 'notistack'
import {makeStyles} from 'tss-react/mui'

import BottomSheetPicker from '@progyny/components/Navigation/BottomSheetPicker'
import {getProductLineLabel} from '@progyny/components/Navigation/Navigation'
import {
  MeDocument,
  useUpdateMemberJourneyMutation,
} from '@progyny/generated/graphql'
import {useProductLine} from '@progyny/hooks/useProductLine'
import {useRoutes, RouteEnum} from '@progyny/routes'
import {useMeStore} from '@progyny/store/meStore'
import {useRefetchContext} from '@progyny/store/refetchStore'

const useStyles = makeStyles()((theme: Theme) => ({
  journeyBtn: {
    paddingLeft: getSpacing(16),
    fontSize: 16,
    fontWeight: 400,
    color: '#251F1F',
    '&:hover': {
      color: '#251F1F',
    },
  },
  activeProductBtn: {
    paddingLeft: 8,
  },
  availableProduct: {
    paddingLeft: 32,
    color: '#251F1F',
    '&:hover': {
      color: '#251F1F',
    },
  },
  expandJourney: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'baseline',
    padding: '0 16px 0 8px',
  },
  journeyAccordion: {
    margin: 0,
    borderBottom: 'none',
    '&.Mui-expanded': {
      margin: '8px 0',
    },
  },
  accordionSummary: {
    '&.Mui-expanded': {
      minHeight: 32,
    },
    '& .MuiAccordionSummary-content': {
      margin: '0px !important',
    },
  },
}))

type SwitchProductProps = {
  variant?: 'desktop' | 'mobile'
  open?: boolean
  onClose?: () => void
}

const SwitchProduct = ({
  variant = 'desktop',
  open = false,
  onClose,
}: SwitchProductProps) => {
  const {classes} = useStyles()
  const {t} = useTranslation()
  const {productLine} = useProductLine()
  const colors = getProductLineColors(productLine)
  const user = useMeStore((s) => s.user)
  const {triggerRefetch} = useRefetchContext()
  const {getPath} = useRoutes()
  const history = useHistory()
  const [updateMemberJourneyMutation] = useUpdateMemberJourneyMutation()
  const [expanded, setExpanded] = useState(false)

  const handleExpansion = () => {
    setExpanded((prev) => !prev)
  }

  const handleSwitchProduct = async (existingProduct: string) => {
    onClose?.()
    try {
      await updateMemberJourneyMutation({
        variables: {
          memberJourney: existingProduct,
          endCurrentJourney: false,
        },
        refetchQueries: [{query: MeDocument}],
        awaitRefetchQueries: true,
      })

      // Only refetch article categories (not individual article queries)
      triggerRefetch('fetchArticleCategories')

      // Redirect to home page after successful journey change
      history.push(getPath(RouteEnum.HOME))

      enqueueSnackbar(
        t('navigation_switch_product_journey_switched_successfully'),
        {variant: 'success'}
      )
    } catch (error) {
      console.error('Error switching journey:', error)
      enqueueSnackbar(t('navigation_switch_product_failed_to_switch_journey'), {
        variant: 'error',
      })
    }
  }

  const journeys = [
    user?.member?.activeMemberJourney,
    ...(user?.member?.previouslySelectedJourneys ?? []),
  ]

  if (variant === 'mobile') {
    return (
      <BottomSheetPicker
        open={open}
        onClose={onClose}
        title={t('navigation_switch_product_explore_my_journeys')}
        items={journeys.map((existingProduct) => ({
          key: existingProduct!,
          label: t(getProductLineLabel(existingProduct!)),
          selected: existingProduct === user?.member?.activeMemberJourney,
        }))}
        onSelect={(key) => handleSwitchProduct(key)}
      />
    )
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={handleExpansion}
      className={classes.journeyAccordion}
      slotProps={{transition: {timeout: 400}}}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls='panel1-content'
        id='panel1-header'
        className={classes.accordionSummary}
      >
        <PGButton
          className={classes.journeyBtn}
          label={t('navigation_switch_product_explore_my_journeys')}
          variant='text'
        />
      </AccordionSummary>

      <AccordionDetails className={classes.expandJourney}>
        {journeys.map((existingProduct) => (
          <Box key={existingProduct} display='flex' alignItems='center'>
            {existingProduct === user?.member?.activeMemberJourney && (
              <CheckCircleOutlineIcon sx={{color: colors.primary}} />
            )}

            <PGButton
              label={t(getProductLineLabel(existingProduct!))}
              variant='text'
              className={
                existingProduct === user?.member?.activeMemberJourney
                  ? classes.activeProductBtn
                  : classes.availableProduct
              }
              onClick={() => handleSwitchProduct(existingProduct!)}
            />
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  )
}

export default SwitchProduct
